import { projectFirestore } from '@/firebase/config';

const isPlainObject = (value) => Object.prototype.toString.call(value) === '[object Object]';

const sortObjectKeysDeep = (value) => {
  if (Array.isArray(value)) {
    return value.map(sortObjectKeysDeep);
  }

  if (!isPlainObject(value)) {
    return value;
  }

  return Object.keys(value)
    .sort((a, b) => a.localeCompare(b, 'th'))
    .reduce((result, key) => {
      result[key] = sortObjectKeysDeep(value[key]);
      return result;
    }, {});
};

const serializeFirestoreValue = (value) => {
  if (value === null || value === undefined) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(serializeFirestoreValue);
  }

  if (typeof value.toDate === 'function') {
    try {
      return value.toDate().toISOString();
    } catch (error) {
      return {
        seconds: value.seconds ?? null,
        nanoseconds: value.nanoseconds ?? null,
      };
    }
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value.latitude === 'number' && typeof value.longitude === 'number') {
    return {
      latitude: value.latitude,
      longitude: value.longitude,
    };
  }

  if (typeof value.path === 'string' && typeof value.id === 'string' && value.firestore) {
    return value.path;
  }

  if (isPlainObject(value)) {
    return Object.keys(value).reduce((result, key) => {
      result[key] = serializeFirestoreValue(value[key]);
      return result;
    }, {});
  }

  return value;
};

const downloadJsonFile = (fileName, jsonText) => {
  const blob = new Blob([`${jsonText}\n`], {
    type: 'application/json;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const copyJsonToClipboard = async (jsonText) => {
  if (!navigator.clipboard?.writeText) {
    return false;
  }

  try {
    await navigator.clipboard.writeText(jsonText);
    return true;
  } catch (error) {
    console.warn('Failed to copy exported JSON to clipboard:', error);
    return false;
  }
};

export const exportCollectionTemplateJson = async ({ collectionName, fileName }) => {
  const snapshot = await projectFirestore.collection(collectionName).get();

  const templateObject = snapshot.docs
    .map((doc) => [doc.id, sortObjectKeysDeep(serializeFirestoreValue(doc.data()))])
    .sort(([a], [b]) => a.localeCompare(b, 'th'))
    .reduce((result, [docId, data]) => {
      result[docId] = data;
      return result;
    }, {});

  const jsonText = JSON.stringify(templateObject, null, 2);
  const copied = await copyJsonToClipboard(jsonText);

  downloadJsonFile(fileName, jsonText);

  return {
    copied,
    count: snapshot.size,
  };
};
